
import { supabase } from "@/integrations/supabase/client";
import { GuideType, GuideStep } from "@/hooks/use-guide-mode";

// Define a type for raw response from Supabase
interface RawGuideContentResponse {
  id: string;
  scenario_type: string;
  title: string;
  description: string;
  steps: any; // This will be parsed from JSON
  created_at: string;
  updated_at: string;
}

// Our processed response type with proper GuideStep[] type
export interface GuideContentResponse {
  id: string;
  scenario_type: string;
  title: string;
  description: string;
  steps: GuideStep[];
  created_at: string;
  updated_at: string;
}

export const fetchGuideContent = async (scenarioType: string): Promise<GuideType | null> => {
  try {
    console.log(`Fetching guide content for scenario: ${scenarioType}`);
    
    const { data, error } = await supabase
      .from('guide_content')
      .select('*')
      .eq('scenario_type', scenarioType.toLowerCase())
      .single();
    
    if (error) {
      console.error('Error fetching guide content:', error);
      return null;
    }
    
    if (!data) {
      console.log('No guide content found for this scenario');
      return null;
    }
    
    // Convert raw response and ensure steps is properly parsed from JSON if needed
    const rawResponse = data as RawGuideContentResponse;
    const response: GuideContentResponse = {
      ...rawResponse,
      steps: Array.isArray(rawResponse.steps) ? rawResponse.steps : JSON.parse(rawResponse.steps as string)
    };
    
    // Transform the response into the expected GuideType format
    return {
      id: response.scenario_type,
      title: response.title,
      description: response.description,
      steps: response.steps
    };
  } catch (error) {
    console.error('Unexpected error fetching guide content:', error);
    return null;
  }
};

export const fetchAllGuides = async (): Promise<Record<string, GuideType>> => {
  try {
    const { data, error } = await supabase
      .from('guide_content')
      .select('*');
    
    if (error) {
      console.error('Error fetching all guides:', error);
      return {};
    }
    
    if (!data || data.length === 0) {
      return {};
    }
    
    // Transform the response into a record of GuideType objects
    const guidesRecord: Record<string, GuideType> = {};
    
    data.forEach((rawItem: RawGuideContentResponse) => {
      // Parse steps if needed
      const steps = Array.isArray(rawItem.steps) ? rawItem.steps : JSON.parse(rawItem.steps as string);
      
      guidesRecord[rawItem.scenario_type] = {
        id: rawItem.scenario_type,
        title: rawItem.title,
        description: rawItem.description,
        steps: steps
      };
    });
    
    return guidesRecord;
  } catch (error) {
    console.error('Unexpected error fetching all guides:', error);
    return {};
  }
};
